import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { signupAPI, SignupDTO, SignupResponse, ApiResponse } from "./signupAPI";

interface SignupState {
  loading: boolean;
  error: string | null;
  success: boolean;
  user: { uuid: string; email: string } | null;
}

const initialState: SignupState = {
  loading: false,
  error: null,
  success: false,
  user: null,
};

// Async thunk for signup
export const signupUser = createAsyncThunk<
  SignupResponse, // return type when resolved
  SignupDTO,      // argument type
  { rejectValue: string }
>("signup/signupUser", async (dto, { rejectWithValue }) => {
  try {
    console.log('Sending signup request:', dto);
    const res: ApiResponse<SignupResponse> = await signupAPI(dto);
    console.log('Signup API response:', res);

    if (res.status && res.data) {
      return res.data;
    } else {
      // Handle different error scenarios
      let errorMessage = res.message || "Signup failed";
      
      if (res.errors) {
        if (typeof res.errors === 'string') {
          errorMessage = res.errors;
        } else if (typeof res.errors === 'object') {
          const firstError = Object.values(res.errors)[0];
          errorMessage = Array.isArray(firstError) ? firstError[0] : firstError as string;
        }
      }
      
      return rejectWithValue(errorMessage);
    }
  } catch (error: any) {
    console.error('Signup error:', error);
    return rejectWithValue("Network error. Please check your connection and try again.");
  }
});

const signupSlice = createSlice({
  name: "signup",
  initialState,
  reducers: {
    clearSignupState(state) {
      state.loading = false;
      state.error = null;
      state.success = false;
      state.user = null;
    },
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signupUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(signupUser.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.user = action.payload;
        state.error = null;
      })
      .addCase(signupUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Signup failed";
        state.success = false;
      });
  },
});

export const { clearSignupState, clearError } = signupSlice.actions;
export default signupSlice.reducer;
